/**
 * お問い合わせのページに SATORI のフォームを差し込む（docs/features/05。pleasanter.org の
 * <c>src/_assets/site/modules/contact-forms.js</c> と同じ作り）。
 *
 * <b>書き出した HTML には入れ物の印（<c>[data-satori-form]</c>）と data-key だけがある。</b>
 * ここで SATORI の決まりの入れ物（<c>#satori__creative_container</c>）と読み込みの <c>&lt;script&gt;</c>（<c>#-_-satori_creative-_-</c>）を作って差し込み、
 * SATORI の <c>creative_set.js</c> がその中にフォームを描く。<b>インラインの script は書かない</b>（CSP。docs/02 §2.1）。
 *
 * - 入力欄が現れたら「読み込んでいます」を消す
 * - 読み込めなかった（遮断など）・決まった時間たっても入力欄が現れないときは、案内（<c>[data-satori-fallback]</c>）を出す。
 *   案内を出したあとに遅れてフォームが現れたら、案内を引っ込める
 * - SATORI が差し込む確認ボタン（<c>#satori__submit_confirm</c>）の二重押しを止める（pleasanter.org と同じ）
 * - <b>送信・送信後の表示（完了の画面・自動返信）は SATORI が受け持つ</b>。計測のイベントはここからは積まない
 */

/** SATORI のフォームの読み込み元（pleasanter.org と同じ。プロトコルは https に決める）。 */
export const satoriScriptSrc = "https://delivery.satr.jp/js/creative_set.js";

/** data-key の形（16 桁の英数字。書き出しの <c>material/satori-forms.ts</c> と同じ）。 */
export const satoriKeyPattern = /^[0-9A-Za-z]{16}$/u;

/** 差し込んでからこの時間たっても入力欄が現れなければ、案内を出す（pleasanter.org と同じ 8 秒）。 */
export const fallbackDelayMs = 8000;

interface SatoriFormOptions {
    readonly src?: string;
    readonly delayMs?: number;
}

const hasFields = (root: Element): boolean => root.querySelector("input, select, textarea") !== null;

/** 確認ボタンの二重押しを止める。<b>ボタンは後から差し込まれるので document で受ける</b>。 */
function preventDoubleSubmit(doc: Document, win: Window): void {
    const view = win as Window & typeof globalThis;
    doc.addEventListener("click", (event) => {
        const target = event.target instanceof view.Element ? event.target : null;
        const button = target?.closest<HTMLButtonElement | HTMLInputElement>("#satori__submit_confirm");
        if (button) {
            button.disabled = true;
        }
    });
}

/** SATORI のフォームを差し込む。お問い合わせのページ（印のあるページ）でだけ動く。 */
export function setupSatoriForm(doc: Document = document, win: Window = window, options: SatoriFormOptions = {}): void {
    const slot = doc.querySelector<HTMLElement>("[data-satori-form]");
    if (!slot) {
        return;
    }
    const loading = slot.querySelector<HTMLElement>("[data-satori-loading]");
    const fallback = doc.querySelector<HTMLElement>("[data-satori-fallback]");
    const settle = (): void => {
        loading?.remove();
    };
    const showFallback = (): void => {
        settle();
        if (fallback) {
            fallback.hidden = false;
        }
    };

    const key = slot.dataset["satoriForm"] ?? "";
    if (!satoriKeyPattern.test(key)) {
        // 書き出しが形を見ているので来ないはず。来たら読み込まずに案内を出す
        showFallback();
        return;
    }

    const container = doc.createElement("div");
    container.id = "satori__creative_container";
    const script = doc.createElement("script");
    script.id = "-_-satori_creative-_-";
    script.src = options.src ?? satoriScriptSrc;
    script.dataset["key"] = key;
    container.append(script);

    const timer = win.setTimeout(() => {
        if (!hasFields(container)) {
            showFallback();
        }
    }, options.delayMs ?? fallbackDelayMs);
    const observer = new (win as Window & typeof globalThis).MutationObserver(() => {
        if (!hasFields(container)) {
            return;
        }
        if (fallback) {
            fallback.hidden = true;
        }
        settle();
        observer.disconnect();
        win.clearTimeout(timer);
    });
    observer.observe(container, { childList: true, subtree: true });
    // 読み込めなかった（遮断・拡張機能など）ときは待たずに出す
    script.addEventListener("error", showFallback);

    preventDoubleSubmit(doc, win);
    slot.append(container);
}
